<template>
  <FullScreenPanel
    :model-value="isOpen"
    :title="$t('commerce.mobileEventDetailsModal.eventDetails')"
    mobile-only
    @close="closeModal"
  >
    <div class="space-y-6">
      <!-- Choose order type -->
      <div>
        <h4 class="dark:text-content-inverse font-medium mb-3 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.chooseOrderType') }}
        </h4>

        <div class="gap-3 grid grid-cols-2">
          <button
            v-for="type in orderTypes"
            :key="type.value"
            type="button"
            class="border font-medium px-4 py-3 rounded-lg text-xs transition-colors"
            :class="getOrderTypeButtonClass(type)"
            @click="() => (pendingOrderType = type.value)"
          >
            <span class="block">{{ type.title }}</span>
          </button>
        </div>
      </div>

      <!-- Event name (optional) -->
      <div>
        <h4 class="dark:text-content-inverse font-medium mb-1 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.eventNameOptional') }}
        </h4>

        <p class="dark:text-content-level-400 mb-3 text-content-level-500 text-xs">
          {{ $t('commerce.mobileEventDetailsModal.thisNameWillShowUpInYour') }}
        </p>

        <div class="relative">
          <input
            v-model="pendingEventName"
            type="text"
            :placeholder="$t('commerce.mobileEventDetailsModal.eventNamePlaceholder')"
            class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 dark:placeholder:text-content-level-400 dark:text-content-inverse focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-content-level-500 px-3 py-2 rounded-lg text-content-level-900 w-full"
          />
        </div>

        <p
          class="mt-1 text-xs"
          :class="
            isEventNameTooLong
              ? 'dark:text-danger-400 text-danger-600'
              : 'dark:text-content-level-400 text-content-level-500'
          "
          :role="isEventNameTooLong ? 'alert' : undefined"
        >
          {{
            $t('commerce.mobileEventDetailsModal.eventNameCounter', {
              count: eventNameLength,
              max: EVENT_NAME_MAX_LENGTH
            })
          }}
        </p>
      </div>

      <!-- Address (hidden on the store page) -->
      <div v-show="!isStorePage">
        <h4 class="dark:text-content-inverse font-medium mb-1 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.location') }}
        </h4>

        <p class="dark:text-content-level-400 mb-3 text-content-level-500 text-xs">
          {{ $t('commerce.mobileEventDetailsModal.youCanAddMoreSpecificDetailsDuring') }}
        </p>

        <div class="relative">
          <AddressSearchInput
            v-if="isOpen"
            v-model="addressValue"
            :placeholder="$t('commerce.mobileEventDetailsModal.locationPlaceholder')"
            size="sm"
            @fallback-clear="pendingFallback.reset"
            @fallback-input="pendingFallback.set"
          />
        </div>
      </div>

      <!-- Date -->
      <div>
        <h4 class="dark:text-content-inverse font-medium mb-3 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.date') }}
        </h4>

        <MobileDatePicker
          v-model="pendingEventDate"
          v-bind="props.timezone === null ? {} : { timezone: props.timezone }"
        />
      </div>

      <!-- Time -->
      <div>
        <h4 class="dark:text-content-inverse font-medium mb-1 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.time') }}
        </h4>

        <p class="dark:text-content-level-400 mb-3 text-content-level-500 text-xs">
          {{ $t('commerce.mobileEventDetailsModal.selectATimeForYourOrder') }}
        </p>

        <div class="relative">
          <ClockIcon
            class="-translate-y-1/2 absolute h-4 left-3 text-content-level-400 top-1/2 transform w-4"
          />

          <select
            v-model="pendingEventTime"
            :disabled="hasNoAvailableTimes"
            class="appearance-none bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 dark:text-content-inverse focus:outline-none focus:ring-2 focus:ring-primary pl-10 pr-3 py-2 rounded-lg text-content-level-900 w-full"
          >
            <option value="">
              {{ $t('commerce.mobileEventDetailsModal.addTime') }}
            </option>

            <option
              v-for="time in timeSlots"
              :key="time"
              :value="time"
            >
              {{ time }}
            </option>
          </select>
        </div>

        <p
          v-if="hasNoAvailableTimes"
          class="bg-warning-50 dark:bg-warning-500/10 dark:text-warning-300 mt-2 px-3 py-3 rounded-lg text-sm text-warning-800"
        >
          {{ $t('commerce.mobileEventDetailsModal.noTimesAreAvailableForThisDate') }}
        </p>

        <p
          v-if="validationError.message.value"
          class="dark:text-danger-400 mt-2 text-danger-600 text-sm"
        >
          {{ validationError.message.value }}
        </p>
      </div>

      <!-- Guests: Group Dining is the only order type with a party size, and it is optional -->
      <div v-if="isGroupDining(pendingOrderType)">
        <h4 class="dark:text-content-inverse font-medium mb-3 text-content-level-900 text-sm">
          {{ $t('commerce.mobileEventDetailsModal.guests') }}
        </h4>

        <div class="relative">
          <input
            v-model="guestCountDraft.input.value"
            type="text"
            inputmode="numeric"
            :aria-label="$t('commerce.mobileEventDetailsModal.guests')"
            :aria-invalid="isGuestCountRejected"
            :placeholder="$t('commerce.mobileEventDetailsModal.guestCountPlaceholder')"
            class="bg-surface-canvas border border-border-level-300 dark:bg-surface-level-800 dark:border-border-level-600 dark:placeholder:text-content-level-400 dark:text-content-inverse focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-content-level-500 px-3 py-2 rounded-lg text-content-level-900 w-full"
          />
        </div>

        <p
          v-if="isGuestCountRejected"
          class="dark:text-danger-400 mt-2 text-danger-600 text-sm"
          role="alert"
        >
          {{ $t('commerce.mobileEventDetailsModal.guestCountInvalid') }}
        </p>

        <p class="dark:text-content-level-400 mt-2 text-content-level-500 text-xs">
          {{ $t('commerce.mobileEventDetailsModal.guestCountHint') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex space-x-3 w-full">
        <button
          v-if="hasSelectedFilters"
          type="button"
          class="border border-border-level-300 dark:border-border-level-600 dark:hover:bg-surface-level-700 dark:text-content-level-300 flex-1 hover:bg-surface-level-50 px-4 py-2 rounded-lg text-content-level-700 transition-colors"
          @click="clearEventDetails"
        >
          {{ $t('commerce.mobileEventDetailsModal.clear') }}
        </button>

        <button
          type="button"
          class="border border-border-level-300 dark:border-border-level-600 dark:hover:bg-surface-level-700 dark:text-content-level-300 flex-1 hover:bg-surface-level-50 px-4 py-2 rounded-lg text-content-level-700 transition-colors"
          @click="closeModal"
        >
          {{ $t('commerce.mobileEventDetailsModal.cancel') }}
        </button>

        <button
          type="button"
          :disabled="!canApplySelection"
          class="bg-primary disabled:cursor-not-allowed disabled:opacity-50 flex-1 hover:bg-primary/90 px-4 py-2 rounded-lg text-content-inverse transition-colors"
          @click="updateEventDetails"
        >
          {{ $t('commerce.mobileEventDetailsModal.update') }}
        </button>
      </div>
    </template>
  </FullScreenPanel>
</template>

<script setup lang="ts">
import { hasText } from '~/utils/hasText'
import { ref, computed, watch } from 'vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLocalizedMessage } from '~/composables/useLocalizedMessage'
import FullScreenPanel from '~/components/shared/dialogs/FullScreenPanel.vue'
import AddressSearchInput from '~/components/AddressSearchInput.vue'
import { useFallbackAddressText } from '~/composables/useFallbackAddressText'
import { useLogger } from '~/composables/useLogger'
import { useSearchStore } from '~/stores/useSearchStore'
import { ClockIcon } from '@heroicons/vue/24/outline'
import { SEARCH_ORDER_TYPES } from '~/constants/search'
import { isOrderTimeSlotAvailable } from '~/utils/isOrderTimeSlotAvailable'
import { convertTo12Hour } from '~/utils/convertTo12Hour'
import { buildTimeOptions } from '~/utils/buildTimeOptions'
import { convertTo24Hour } from '~/utils/convertTo24Hour'
import type { TimeSlotStore } from '~/utils/TimeSlotStore'
import type { AddressSearchValue } from '~/lib/geo-providers/interfaces'
import MobileDatePicker from './MobileDatePicker.vue'
import { OrderType } from '~/enums'
import { EVENT_NAME_MAX_LENGTH } from '~/utils/EVENT_NAME_MAX_LENGTH'
import { normalizeEventName } from '~/utils/normalizeEventName'
import { useGuestCountDraft } from '~/composables/useGuestCountDraft'
import { isGroupDining } from '~/utils/isGroupDining'

interface AvailableMenuSchedule {
  day_of_week?: unknown
  day?: unknown
  start_time?: unknown
  end_time?: unknown
  is_enabled?: unknown
  enabled?: unknown
  is_available?: unknown
  specific_date?: unknown
}

interface AvailableMenu {
  status?: unknown
  is_active?: unknown
  availability_mode?: unknown
  sync_with_store_hours?: unknown
  schedule?: AvailableMenuSchedule[]
  custom_hours?: AvailableMenuSchedule[]
  days?: unknown[]
  start_time?: unknown
  end_time?: unknown
}

const props = withDefaults(
  defineProps<{
    /** Controls whether the modal is open. */
    isOpen?: boolean
    /** Whether the modal is rendered on a store page. */
    isStorePage?: boolean
    /** Timezone used for event and availability formatting. */
    timezone?: string | null
    /** Store associated with the event details. */
    store?: TimeSlotStore | null
    /** Menus available for the selected store. */
    menus?: AvailableMenu[]
  }>(),
  {
    menus: () => [],
    store: null,
    timezone: null
  }
)

const emit = defineEmits<{ close: []; update: [value: EventDetailsUpdate] }>()

interface EventDetailsUpdate {
  orderType: OrderType
  eventName: string | null
  address: string | null
  coordinates: { latitude: number; longitude: number } | null
  eventDate: string | null
  eventTime: string | null
  guestCount: number | null
}
const toCoordinateNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0

  const coordinate = Number(value)
  return Number.isNaN(coordinate) ? 0 : coordinate
}

const t = usePresentationTranslator()
const logger = useLogger('MobileEventDetailsModal')
const searchStore = useSearchStore()

const orderTypes = SEARCH_ORDER_TYPES

const getOrderTypeButtonClass = (type: (typeof orderTypes)[number]): string => {
  if (pendingOrderType.value === type.value) {
    return 'bg-primary text-content-inverse border-primary'
  }

  return 'bg-surface-canvas dark:bg-surface-level-800 border-border-level-300 dark:border-border-level-600 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
}

// Full day of 15-minute slots; store rules filter this list after a date is selected.
const allTimeSlots = computed(() =>
  buildTimeOptions(15, convertTo12Hour).map((option) => option.label)
)
const validationError = useLocalizedMessage()
const timeSlotStore = computed(() => {
  let timezone: string | null = null
  if (hasText(props.timezone)) {
    timezone = props.timezone
  } else if (hasText(props.store?.timezone)) {
    timezone = props.store.timezone
  }
  return { ...(props.store ?? {}), timezone }
})

const timeSlots = computed(() =>
  allTimeSlots.value.filter(
    (time) =>
      !pendingEventDate.value ||
      isOrderTimeSlotAvailable(convertTo24Hour(time), {
        eventDate: pendingEventDate.value,
        menus: props.menus,
        orderType: pendingOrderType.value,
        store: timeSlotStore.value
      })
  )
)
const hasNoAvailableTimes = computed(
  () => Boolean(pendingEventDate.value) && timeSlots.value.length === 0
)
const canApplySelection = computed(() => {
  if (hasNoAvailableTimes.value) return false
  if (isEventNameTooLong.value) return false
  // A party size is optional, but a typed one must be valid before it can narrow the search.
  if (isGroupDining(pendingOrderType.value) && isGuestCountRejected.value) return false
  if (!pendingEventDate.value || !pendingEventTime.value) return true

  return isOrderTimeSlotAvailable(convertTo24Hour(pendingEventTime.value), {
    eventDate: pendingEventDate.value,
    menus: props.menus,
    orderType: pendingOrderType.value,
    store: timeSlotStore.value
  })
})

// Pending state
const pendingOrderType = ref<OrderType>(OrderType.DINE_IN)
const pendingEventName = ref('')
const pendingAddress = ref('')
const pendingEventDate = ref<string>('')
const pendingEventTime = ref<string>('')
const guestCountDraft = useGuestCountDraft()
const isGuestCountRejected = computed(
  () => guestCountDraft.input.value.trim() !== '' && !guestCountDraft.count.value.provided
)
const eventNameLength = computed(() => Array.from(pendingEventName.value.trim()).length)
const isEventNameTooLong = computed(() => eventNameLength.value > EVENT_NAME_MAX_LENGTH)
const pendingCoordinates = ref<AddressSearchValue | null>(null)
const pendingFallback = useFallbackAddressText()

// Address search value
const addressValue = ref<AddressSearchValue | null>(null)

// Watch address selection for pending state
watch(addressValue, (val) => {
  if (val) {
    pendingAddress.value = val.address
    pendingCoordinates.value = { address: val.address, lat: val.lat, lng: val.lng }
  }
})

const hasSelectedFilters = computed(() => {
  return Boolean(
    pendingOrderType.value !== OrderType.DINE_IN ||
    pendingEventName.value ||
    pendingAddress.value ||
    pendingEventDate.value ||
    pendingEventTime.value
  )
})

// Initialize from current search store values
const initializeFromStore = () => {
  validationError.clear()
  pendingOrderType.value = searchStore.orderType
  pendingEventName.value = hasText(searchStore.eventDetails?.eventName)
    ? searchStore.eventDetails.eventName
    : ''
  pendingAddress.value = getCurrentAddress()
  const eventDate = searchStore.eventDateTime?.eventDate
  let normalizedEventDate = ''
  if (eventDate instanceof Date) {
    normalizedEventDate = eventDate.toISOString().slice(0, 10)
  } else if (hasText(eventDate)) {
    normalizedEventDate = eventDate
  }
  pendingEventDate.value = normalizedEventDate
  const storedEventTime = searchStore.eventDateTime?.eventTime
  pendingEventTime.value = hasText(storedEventTime) ? convertTo12Hour(storedEventTime) : ''
  guestCountDraft.reset(searchStore.eventDetails?.guestCount)
  pendingFallback.reset()

  // Sync addressValue for AddressSearchInput (same pattern as LocationFilter)
  if (pendingAddress.value) {
    addressValue.value = {
      address: pendingAddress.value,
      lat: toCoordinateNumber(searchStore.coordinates?.lat),
      lng: toCoordinateNumber(searchStore.coordinates?.lng)
    }
  } else {
    addressValue.value = null
  }
}

// Get current address from search store
const getCurrentAddress = () => {
  if (typeof searchStore.searchQuery === 'string') {
    return searchStore.searchQuery
  } else if (hasText(searchStore.searchQuery?.address)) {
    return searchStore.searchQuery.address
  } else if (hasText(searchStore.searchQuery?.q)) {
    return searchStore.searchQuery.q
  }
  return ''
}

// Watch for modal open to initialize values
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      initializeFromStore()
    }
  }
)

// Close modal
const closeModal = () => {
  emit('close')
}

const clearAutocompleteInput = () => {
  addressValue.value = null
}

const resetPendingState = () => {
  pendingOrderType.value = OrderType.DINE_IN
  pendingEventName.value = ''
  pendingAddress.value = ''
  pendingEventDate.value = ''
  pendingEventTime.value = ''
  pendingCoordinates.value = null
  pendingFallback.reset()
}

// Clear starts the search over but keeps the party size: it is what a Group Dining search needs.
const clearEventDetails = () => {
  const keptGuestCount = searchStore.guestCount
  resetPendingState()
  guestCountDraft.reset(keptGuestCount.provided ? keptGuestCount.value : null)
  clearAutocompleteInput()

  searchStore.setOrderType(OrderType.DINE_IN)
  searchStore.searchQuery = null
  searchStore.coordinates = null
  searchStore.bbox = null
  searchStore.place_type = null
  searchStore.eventDetails = keptGuestCount.provided ? { guestCount: keptGuestCount.value } : null
  searchStore.eventDateTime = null

  emit('update', {
    address: null,
    coordinates: null,
    eventDate: null,
    eventName: null,
    eventTime: null,
    guestCount: keptGuestCount.provided ? keptGuestCount.value : null,
    orderType: OrderType.DINE_IN
  })

  closeModal()
}

// Update event details
const updateEventDetails = async () => {
  try {
    // A party size is optional for Group Dining, but a typed invalid one is not saved; Pickup keeps what is stored.
    const pendingGuestCount = isGroupDining(pendingOrderType.value)
      ? guestCountDraft.attempt()
      : searchStore.guestCount
    if (isGroupDining(pendingOrderType.value) && isGuestCountRejected.value) return

    const convertedTime = convertTo24Hour(pendingEventTime.value)

    if (
      pendingEventDate.value &&
      convertedTime &&
      !isOrderTimeSlotAvailable(convertedTime, {
        eventDate: pendingEventDate.value,
        menus: props.menus,
        orderType: pendingOrderType.value,
        store: timeSlotStore.value
      })
    ) {
      validationError.set(() =>
        hasNoAvailableTimes.value
          ? t('commerce.mobileEventDetailsModal.noTimesAreAvailableForThisDate')
          : t('commerce.mobileEventDetailsModal.selectAnAvailableEventTime')
      )
      return
    }

    const currentAddress = getCurrentAddress()
    const locationChanged =
      pendingAddress.value.length > 0 &&
      pendingAddress.value !== currentAddress &&
      pendingCoordinates.value !== null

    if (pendingFallback.hasLocatableText.value) {
      searchStore.applyAddressText(pendingFallback.text.value)
    } else if (locationChanged) {
      searchStore.searchQuery = pendingAddress.value
      searchStore.coordinates = pendingCoordinates.value
    }

    searchStore.setOrderType(pendingOrderType.value)

    // Group Dining always writes its party size, so clearing the field also clears the filter.
    const hasEventDetailsToSave =
      isGroupDining(pendingOrderType.value) ||
      pendingEventName.value.length > 0 ||
      pendingGuestCount.provided
    if (hasEventDetailsToSave) {
      searchStore.setEventDetails({
        eventName: pendingEventName.value,
        guestCount: pendingGuestCount.provided ? pendingGuestCount.value : null
      })
    }

    if (pendingEventDate.value || pendingEventTime.value) {
      searchStore.eventDateTime = {
        eventDate: pendingEventDate.value,
        eventTime: convertedTime
      }
    }

    emit('update', {
      address: pendingFallback.hasLocatableText.value
        ? pendingFallback.text.value.trim()
        : pendingAddress.value,
      coordinates:
        pendingCoordinates.value && !pendingFallback.hasLocatableText.value
          ? {
              latitude: pendingCoordinates.value.lat,
              longitude: pendingCoordinates.value.lng
            }
          : null,
      eventDate: pendingEventDate.value,
      eventName: normalizeEventName(pendingEventName.value),
      eventTime: convertedTime,
      guestCount: pendingGuestCount.provided ? pendingGuestCount.value : null,
      orderType: pendingOrderType.value
    })

    // No redirect needed - the watchers on search/store/event pages will handle updates
    closeModal()
  } catch (error) {
    logger.error('updateEventDetails', { error })
    closeModal()
  }
}

const clearUnavailableEventTime = (eventDate: string): void => {
  if (
    pendingEventTime.value &&
    !isOrderTimeSlotAvailable(convertTo24Hour(pendingEventTime.value), {
      eventDate,
      menus: props.menus,
      orderType: pendingOrderType.value,
      store: timeSlotStore.value
    })
  ) {
    pendingEventTime.value = ''
    validationError.set(() =>
      timeSlots.value.length
        ? t('commerce.mobileEventDetailsModal.selectAnAvailableEventTime')
        : t('commerce.mobileEventDetailsModal.noTimesAreAvailableForThisDate')
    )
  }
}

watch(pendingEventDate, (eventDate) => {
  validationError.clear()
  if (eventDate) clearUnavailableEventTime(eventDate)
})

watch(pendingOrderType, () => {
  validationError.clear()
  if (pendingEventDate.value) clearUnavailableEventTime(pendingEventDate.value)
})

watch(pendingEventTime, () => {
  validationError.clear()
})
</script>
